import { useEffect, useMemo, useState } from "react";
import { getUsers } from "../api/userApi";
import MainLayout from "../../layouts/MainLayout";
import PageLoader from "../../components/Loader/PageLoader";
import TablePagination from "../../components/TablePagination";
import { useNavigate } from "react-router-dom";

function UserList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const navigate = useNavigate();

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await getUsers();
      setUsers(res.data.data || []);
    } catch (error) {
      console.error(error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return users;
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(keyword) ||
        u.mobile_number?.toLowerCase().includes(keyword) ||
        u.role?.toLowerCase().includes(keyword) ||
        u.unique_id?.toString().toLowerCase().includes(keyword)
    );
  }, [users, search]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize) || 1);
  const safePage = Math.min(page, totalPages);
  const pageRows = filteredUsers.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize
  );

  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  const handlePageSize = (size) => {
    setPageSize(size);
    setPage(1);
  };

  return (
    <MainLayout>
      <div className="wd-page-head">
        <div>
          <h3 className="page-title">Users</h3>
          <p className="page-subtitle">Manage portal accounts and roles</p>
        </div>
        <button
          className="btn btn-primary-custom"
          onClick={() => navigate("/account/register")}
        >
          + Add User
        </button>
      </div>

      <div className="wd-table-card">
        <div className="wd-table-toolbar">
          <div className="wd-search">
            <i className="bi bi-search" aria-hidden="true" />
            <input
              type="search"
              className="form-control"
              placeholder="Search by name, mobile, role, ID…"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
          <span className="wd-table-count">
            {filteredUsers.length} user{filteredUsers.length === 1 ? "" : "s"}
          </span>
        </div>

        {loading ? (
          <PageLoader label="Loading users…" />
        ) : filteredUsers.length === 0 ? (
          <div className="wd-empty">
            <i className="bi bi-people" aria-hidden="true" />
            <p>No matching users found</p>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table wd-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Mobile</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((u) => (
                    <tr key={u.id}>
                      <td className="wd-mono">{u.unique_id}</td>
                      <td>
                        <div className="wd-user-cell">
                          <span className="wd-avatar">
                            {(u.name || "?").charAt(0).toUpperCase()}
                          </span>
                          <span className="fw-semibold">{u.name}</span>
                        </div>
                      </td>
                      <td>{u.mobile_number}</td>
                      <td>
                        <span className="wd-badge wd-badge-muted">{u.role}</span>
                      </td>
                      <td>
                        <span
                          className={`wd-badge ${
                            u.status === "active"
                              ? "wd-badge-success"
                              : "wd-badge-danger"
                          }`}
                        >
                          {u.status}
                        </span>
                      </td>
                      <td className="text-end">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => navigate(`/account/EditUser/${u.id}`)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <TablePagination
              page={safePage}
              pageSize={pageSize}
              total={filteredUsers.length}
              onPageChange={setPage}
              onPageSizeChange={handlePageSize}
            />
          </>
        )}
      </div>
    </MainLayout>
  );
}

export default UserList;
